/** The games the golden bottle draws from (the quiz is its own session, see quiz/QuizSession.ts). */
export const MiniGameKind = {
  Crane: 'CRANE',
  Hammer: 'HAMMER',
  Mixer: 'MIXER',
} as const;
export type MiniGameKind = (typeof MiniGameKind)[keyof typeof MiniGameKind];

/** How well one attempt (strike, set-down, stop) went – drives the sound and the label. */
export const AttemptGrade = {
  Perfect: 'PERFECT',
  Good: 'GOOD',
  Miss: 'MISS',
} as const;
export type AttemptGrade = (typeof AttemptGrade)[keyof typeof AttemptGrade];

export interface MiniGameAttempt {
  readonly grade: AttemptGrade;
  /** What this attempt added to the share (0 … 1). */
  readonly gained: number;
}

export interface MiniGameOutcome {
  readonly kind: MiniGameKind;
  /** Reached share of the bonus (0 … 1). */
  readonly share: number;
  /** Bottles won: the round's bonus times the share, rounded down. */
  readonly bonus: number;
}

export const MiniGamePhase = {
  Idle: 'IDLE',
  Playing: 'PLAYING',
  /** All attempts made: the result stays for a moment. */
  Result: 'RESULT',
} as const;
export type MiniGamePhase = (typeof MiniGamePhase)[keyof typeof MiniGamePhase];

/**
 * One mini game, engine- and DOM-free: the clock runs in real seconds while the world is frozen. `tap` is the one
 * action (strike, drop, stop); `update` returns attempts as they are judged and the game's own state for drawing.
 */
export interface MiniGame {
  readonly kind: MiniGameKind;
  /** 0 … 1, what the attempts so far reached. */
  readonly share: number;
  /** All attempts made (or timed out). */
  readonly finished: boolean;
  /** Attempts made so far / in total (HUD dots). */
  readonly attemptsMade: number;
  readonly attemptsTotal: number;
  /** 0 … 1 of the current attempt's time left (1 while an animation runs). */
  readonly timeShare: number;
  /** One tap. Ignored while an animation runs. */
  tap(): void;
  /** Advances the game; returns the attempt judged in this step, if any. */
  update(dt: number): MiniGameAttempt | null;
}

/** Share of an attempt by its miss distance: `near` just outside perfect, linearly down to `edge` at the zone edge. */
export function gradedShare(distance: number, perfect: number, zone: number, near: number, edge: number): number {
  if (distance <= perfect) return 1;
  if (distance > zone) return 0;
  const t = (distance - perfect) / Math.max(1e-6, zone - perfect);
  return near + (edge - near) * t;
}

export const gradeOf = (distance: number, perfect: number, zone: number): AttemptGrade =>
  distance <= perfect ? AttemptGrade.Perfect : distance <= zone ? AttemptGrade.Good : AttemptGrade.Miss;
